// @ts-ignore - lib/billing is plain JS
import {
  ADDON_PRODUCTS,
  ADDONS,
  BILLING_CYCLES,
  CHECKOUT_PRICING,
  DEDICATED_FUTURES_FLAT_CENTS,
  DEDICATED_KINDS,
  DE_SUPPORTED_CYCLES,
  PRODUCTS
} from '../../lib/billing/config';
// @ts-ignore - lib/billing is plain JS
import { appendDedicatedEnv } from '../../lib/billing/dedicatedSelection';
// @ts-ignore - lib/billing is plain JS
import { resolveCatalogPrice, computeLineAmountCents, priceMeta } from '../../lib/billing/priceCatalog';
import { CFD_PLATFORMS, FUTURES_PLATFORMS, PlatformLogo } from './platformLogos';

/**
 * The quick-subscribe catalog. ONE entry per product — adding a product to
 * the dialog means adding an entry here, nothing in the dialog component.
 *
 * Each entry answers three questions:
 *   - how it reads (title / meta / badge),
 *   - what it costs (price(catalog, opts) → display cents),
 *   - what it stages (stage(catalog, opts) → selection patch for checkout).
 * `configurable: true` additionally shows the tier + account controls;
 * everything else renders as a fixed one-price row.
 *
 * Every price is for the billing cycle in `opts.cycle` (monthly by default):
 * live catalog SKU for that exact cycle, else the contract fallback × the
 * cycle's paid months.
 */

export type QuickTier = 'standard' | 'premium';

export interface QuickOpts {
  tier: QuickTier;
  accounts: number;
  /** Billing cycle id (BILLING_CYCLES). Absent = monthly. */
  cycle?: string;
  /** Futures accounts on top of the included bundle (pay-as-you-go only). */
  futuresExtra?: number;
  /** Selected mode per product key (see QuickProduct.modes). Absent = first mode. */
  modes?: Record<string, string>;
  /** Analyzer add-on toggled onto a copier cart. */
  analyzer?: boolean;
  /** Product ids whose one-time trial the user already consumed (BillingOverview). */
  trialUsedProductIds?: string[];
}

export const MONTHLY: string = BILLING_CYCLES.monthly;

/** The cycle an options object asks for. */
export const cycleOf = (opts?: { cycle?: string } | null): string => opts?.cycle || MONTHLY;

/** Months the buyer pays for on a cycle (semi-annual pays five, annual ten). */
export const paidMonths = (cycle: string): number =>
  (Number((CHECKOUT_PRICING.cycleMultipliers as Record<string, number>)[cycle]) || 1) -
  (Number((CHECKOUT_PRICING.cycleMonthsFree as Record<string, number>)[cycle]) || 0);

/** Cycles a product can be bought on (dedicated environments: monthly + annual only). */
export const cyclesForProduct = (key: string, all: string[]): string[] =>
  key === 'dedicated' ? all.filter((c) => (DE_SUPPORTED_CYCLES as Set<string>).has(c)) : all;

/** Selected mode for a product, defaulting to its first purchasable mode. */
export const selectedMode = (p: QuickProduct, catalog: unknown, opts: QuickOpts): string | null => {
  if (!p.modes) return null;
  const available = p.modes(catalog, opts).filter((m) => m.cents != null);
  if (!available.length) return null;
  const picked = opts.modes?.[p.key];
  return available.some((m) => m.key === picked) ? (picked as string) : available[0].key;
};

export interface QuickProduct {
  key: string;
  title: string;
  /** One short line under the title. Keep it to platforms / the hook. */
  meta: string;
  /** Platform icons shown instead of the meta text when present. */
  platforms?: PlatformLogo[];
  /** Optional chip next to the title (e.g. a trial). Computed so entries can
   *  hide it when it doesn't apply (trial already consumed). */
  badge?: (catalog: unknown, opts: QuickOpts) => string | null;
  /** Shows tier pills + account stepper when selected. */
  configurable?: boolean;
  /** Mutually-exclusive purchase modes (futures: pay-as-you-go vs unlimited).
   *  A mode with null cents isn't purchasable right now and is hidden. */
  modes?: (catalog: unknown, opts?: QuickOpts) => Array<{ key: string; label: string; detail: string; cents: number | null }>;
  /** Display price in cents for the current options. */
  price: (catalog: unknown, opts: QuickOpts) => number;
  /** Selection patch handed to the checkout store. */
  stage: (catalog: unknown, opts: QuickOpts) => Record<string, unknown>;
}

/** The catalog SKU for product + cycle, only when it really is that cycle (and variant). */
const skuFor = (catalog: unknown, product: string, cycle: string, variant?: string) => {
  const price = catalog ? resolveCatalogPrice(catalog as any, product, cycle, variant ? { variant } : {}) : null;
  const meta = priceMeta(price) as { cycle?: string; variant?: string } | null;
  const ok = price?.id && meta?.cycle === cycle && (meta?.variant ?? 'default') === (variant ?? 'default');
  return ok ? price : null;
};

/** Live catalog price for `qty` units on `cycle`, else the monthly contract total × paid months. */
const linePriceCents = (catalog: unknown, product: string, qty: number, fallbackMonthlyCents: number, cycle: string = MONTHLY): number => {
  const price = skuFor(catalog, product, cycle);
  return price ? computeLineAmountCents(price, qty) : fallbackMonthlyCents * paidMonths(cycle);
};

/** Flat price of a futures dedicated environment (unlimited accounts). */
export const dedicatedFuturesMonthlyCents = (catalog: unknown, cycle: string = MONTHLY): number =>
  linePriceCents(catalog, ADDON_PRODUCTS.dedicatedFutures, 1, DEDICATED_FUTURES_FLAT_CENTS[MONTHLY], cycle);

/** Accounts bundled into the futures base fee (live tier boundary, else contract). */
export const futuresIncluded = (catalog: unknown, cycle: string = MONTHLY): number => {
  const price = skuFor(catalog, PRODUCTS.futures, cycle);
  return Number(price?.tiers?.[0]?.up_to) || CHECKOUT_PRICING.futures.tieredIncludedAccounts;
};

export const cfdTierCents = (catalog: unknown, tier: QuickTier, cycle: string = MONTHLY): number =>
  tier === 'standard'
    ? linePriceCents(catalog, PRODUCTS.standard, 1, CHECKOUT_PRICING.copier.standardPerAccountCents, cycle)
    : linePriceCents(catalog, PRODUCTS.premium, 1, CHECKOUT_PRICING.copier.premiumPerAccountCents, cycle);

/** Flat futures base fee (covers the included bundle). */
export const futuresBaseCents = (catalog: unknown, cycle: string = MONTHLY): number =>
  linePriceCents(catalog, PRODUCTS.futures, futuresIncluded(catalog, cycle), CHECKOUT_PRICING.futures.firstAccountMonthlyCents, cycle);

/** The futures licence at the bundle plus `extra` accounts, from the same graduated tiers the checkout bills. */
export const futuresCents = (catalog: unknown, extra: number, cycle: string = MONTHLY): number => {
  const n = Math.max(0, Math.round(extra || 0));
  const included = futuresIncluded(catalog, cycle);
  const price = skuFor(catalog, PRODUCTS.futures, cycle);
  if (price) return computeLineAmountCents(price, included + n);
  return (CHECKOUT_PRICING.futures.firstAccountMonthlyCents + n * CHECKOUT_PRICING.futures.extraAccountMonthlyCents) * paidMonths(cycle);
};

/** Analyzer add-on price on a cycle. */
export const analyzerCents = (catalog: unknown, cycle: string = MONTHLY): number =>
  linePriceCents(catalog, ADDON_PRODUCTS.analyzer, 1, ADDONS.analyzer.priceCentsMonthly, cycle);
/** Kept for callers that quote the add-on monthly. */
export const analyzerMonthlyCents = (catalog: unknown): number => analyzerCents(catalog, MONTHLY);

/** Dedicated Environment base (one env, one included account). */
export const dedicatedMonthlyCents = (catalog: unknown, cycle: string = MONTHLY): number =>
  linePriceCents(catalog, ADDON_PRODUCTS.dedicated, 1, ADDONS.dedicated.basePriceCentsMonthly, cycle);

/** Flat "unlimited accounts" futures SKU (live catalog only — no contract fallback). */
export const futuresUnlimitedCents = (catalog: unknown, cycle: string = MONTHLY): number | null => {
  const price = skuFor(catalog, PRODUCTS.futures, cycle, 'unlimited');
  return price ? computeLineAmountCents(price, 1) : null;
};

/**
 * Why the futures trial is off, if it is: 'used' once this account has consumed
 * it, 'none' when the SKU carries no trial, null while the catalog loads.
 */
export const futuresTrialBlock = (
  catalog: unknown,
  trialUsedProductIds: string[] = [],
  cycle: string = MONTHLY
): 'used' | 'none' | null => {
  const price = catalog ? skuFor(catalog, PRODUCTS.futures, cycle) : null;
  if (!price) return null;
  if (!(Number(price?.trial_period_days ?? 0) > 0)) return 'none';
  const productId = price?.productId || price?.product_id || '';
  return productId && trialUsedProductIds.includes(productId) ? 'used' : null;
};

/**
 * Whether the futures one-time trial still applies for this user. Derived from
 * futuresTrialBlock so the two can never drift — and deliberately FALSE while
 * the catalog is still loading: the old optimistic `true` promised "$0 today"
 * on a screen that had no prices yet, to users who had already spent the trial.
 */
export const futuresTrialAvailable = (catalog: unknown, trialUsedProductIds: string[] = [], cycle: string = MONTHLY): boolean => {
  const price = catalog ? skuFor(catalog, PRODUCTS.futures, cycle) : null;
  return Boolean(price) && futuresTrialBlock(catalog, trialUsedProductIds, cycle) === null;
};

// Mode lookup that avoids a forward reference to QUICK_PRODUCTS inside entries.
const selectedModeKey = (catalog: unknown, opts: QuickOpts, key: string): string | null => {
  const p = QUICK_PRODUCTS.find((q) => q.key === key);
  return p ? selectedMode(p, catalog, opts) : null;
};

export const QUICK_PRODUCTS: QuickProduct[] = [
  {
    key: 'cfd',
    title: 'CFD Copier',
    meta: 'MT4 · MT5 · cTrader · more',
    platforms: CFD_PLATFORMS,
    configurable: true,
    // Priced through the tier calculator at the FULL quantity (not unit ×
    // count): tiered/graduated Stripe prices are not linear, and the payment
    // step charges the tiered amount.
    price: (catalog, opts) => {
      const n = Math.max(2, opts.accounts);
      return opts.tier === 'standard'
        ? linePriceCents(catalog, PRODUCTS.standard, n, CHECKOUT_PRICING.copier.standardPerAccountCents * n, cycleOf(opts))
        : linePriceCents(catalog, PRODUCTS.premium, n, CHECKOUT_PRICING.copier.premiumPerAccountCents * n, cycleOf(opts));
    },
    // A TOTAL patch over every copier key, never a partial one: these carts
    // merge onto whatever was staged before, so omitting a key left the
    // previous route's or tier's line in the drawer and billed both.
    stage: (_catalog, opts) => ({
      billingCycle: cycleOf(opts),
      standardCount: opts.tier === 'premium' ? 0 : Math.max(2, opts.accounts),
      premiumCount: opts.tier === 'premium' ? Math.max(2, opts.accounts) : 0,
      futuresCount: 0,
      futuresUnlimited: false
    })
  },
  {
    key: 'futures',
    title: 'Futures Copier',
    meta: 'Tradovate · Rithmic · NinjaTrader',
    platforms: FUTURES_PLATFORMS,
    // The trial rides the tiered SKU only — the unlimited SKU has no tiers and
    // is never trial-capable, so the badge drops when Unlimited is selected.
    badge: (catalog, opts) =>
      selectedModeKey(catalog, opts, 'futures') !== 'unlimited' &&
      !(opts.futuresExtra && opts.futuresExtra > 0) &&
      futuresTrialAvailable(catalog, opts.trialUsedProductIds, cycleOf(opts))
        ? `${CHECKOUT_PRICING.futures.trialDays}-day free trial`
        : null,
    modes: (catalog, opts) => [
      {
        key: 'payg',
        label: 'Pay as you go',
        detail: `${futuresIncluded(catalog, cycleOf(opts))} included`,
        cents: futuresBaseCents(catalog, cycleOf(opts))
      },
      { key: 'unlimited', label: 'Unlimited', detail: 'one flat price', cents: futuresUnlimitedCents(catalog, cycleOf(opts)) }
    ],
    price: (catalog, opts) =>
      (selectedModeKey(catalog, opts, 'futures') === 'unlimited' && futuresUnlimitedCents(catalog, cycleOf(opts))) ||
      futuresCents(catalog, opts.futuresExtra ?? 0, cycleOf(opts)),
    // Unlimited is a flat whole-product SKU billed as qty=1 (mirrors the
    // drawer's setFuturesUnlimited seeding).
    stage: (catalog, opts) =>
      selectedModeKey(catalog, opts, 'futures') === 'unlimited' && futuresUnlimitedCents(catalog, cycleOf(opts))
        ? { billingCycle: cycleOf(opts), standardCount: 0, premiumCount: 0, futuresUnlimited: true, futuresCount: 1 }
        : {
            billingCycle: cycleOf(opts),
            standardCount: 0,
            premiumCount: 0,
            futuresUnlimited: false,
            futuresCount: futuresIncluded(catalog, cycleOf(opts)) + Math.max(0, opts.futuresExtra ?? 0)
          }
  },
  {
    // One cart, both markets: a 1-account CFD line plus the futures licence.
    // The selection schema already carries multiple product lines — this row
    // just stages them together instead of making the user buy twice.
    key: 'both',
    title: 'CFD + Futures Copier',
    meta: 'Copy across both markets — one checkout',
    platforms: [...CFD_PLATFORMS, ...FUTURES_PLATFORMS],
    // No trial badge even when the futures side qualifies: the CFD line still
    // charges today, so a "$0 today" framing would be wrong. If the trial
    // applies, payment simply charges less than the number shown.
    // The CFD side of the pair takes the same tiers as a CFD-only cart.
    price: (catalog, opts) => {
      const n = Math.max(1, opts.accounts);
      const cfd =
        opts.tier === 'premium'
          ? linePriceCents(catalog, PRODUCTS.premium, n, CHECKOUT_PRICING.copier.premiumPerAccountCents * n, cycleOf(opts))
          : linePriceCents(catalog, PRODUCTS.standard, n, CHECKOUT_PRICING.copier.standardPerAccountCents * n, cycleOf(opts));
      return cfd + futuresCents(catalog, opts.futuresExtra ?? 0, cycleOf(opts));
    },
    stage: (catalog, opts) => ({
      billingCycle: cycleOf(opts),
      standardCount: opts.tier === 'premium' ? 0 : Math.max(1, opts.accounts),
      premiumCount: opts.tier === 'premium' ? Math.max(1, opts.accounts) : 0,
      futuresUnlimited: false,
      futuresCount: futuresIncluded(catalog, cycleOf(opts)) + Math.max(0, opts.futuresExtra ?? 0)
    })
  },
  {
    key: 'dedicated',
    title: 'Dedicated Environment',
    // Two envs, two prices: CFD with one account included, futures a
    // cheaper flat rate with unlimited accounts — the mode pills carry both.
    meta: 'Your own IP & hardware · MetaTrader & futures',
    modes: (catalog, opts) => [
      { key: DEDICATED_KINDS.cfd, label: 'CFD env', detail: '1 account included', cents: dedicatedMonthlyCents(catalog, cycleOf(opts)) },
      {
        key: DEDICATED_KINDS.futures,
        label: 'Futures env',
        detail: 'unlimited accounts',
        cents: dedicatedFuturesMonthlyCents(catalog, cycleOf(opts))
      }
    ],
    price: (catalog, opts) => {
      const kind = selectedModeKey(catalog, opts, 'dedicated') || DEDICATED_KINDS.cfd;
      return kind === DEDICATED_KINDS.futures
        ? dedicatedFuturesMonthlyCents(catalog, cycleOf(opts))
        : dedicatedMonthlyCents(catalog, cycleOf(opts));
    },
    // A DE is purchasable on its own — one env, one included account.
    stage: (catalog, opts) => ({
      billingCycle: cycleOf(opts),
      ...appendDedicatedEnv(null, selectedModeKey(catalog, opts, 'dedicated') || DEDICATED_KINDS.cfd)
    })
  }
];
